import { getBaseUrl } from '../config/apiConfig';

const PROXY_ENDPOINT = '/api/media/image-proxy';

/**
 * Domains whose CDN responses include Cross-Origin-Resource-Policy: same-origin
 * or similar headers that block direct embedding on third-party sites.
 */
const CORS_BLOCKED_PATTERNS = [
  'cdninstagram.com',
  'scontent',
  'fbcdn.net',
  'instagram.com',
];

/**
 * The letters a tile prints for a name: the first letter or digit of each of its
 * first two words, or of the first word only for a brand's mark (a logo-less brand
 * tile prints one initial). Punctuation and emoji are skipped — "@willtennyson" is W,
 * not @ — and a letter is taken whole, never half a surrogate pair.
 */
function tileLetters(name: string | undefined, mark: boolean): string[] {
  return (name || '').trim().split(/\s+/)
    .map(word => word.match(/[\p{L}\p{N}]/u)?.[0].toUpperCase())
    .filter((letter): letter is string => Boolean(letter))
    .slice(0, mark ? 1 : 2);
}

// btoa only takes Latin-1 and an initial often is not (李, Ł): encode the SVG as UTF-8
// bytes first, or the swap throws inside the <img>'s onError and the logo stays broken.
const utf8Base64 = (text: string) =>
  btoa(encodeURIComponent(text).replace(/%([0-9A-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16))));

/**
 * Generate an SVG data-URI placeholder avatar from a name's initials.
 * Used as a fallback when an image URL is missing or fails to load.
 *
 * A person is drawn as the desk's portrait tile (initials in ink on manila with
 * a paper border — see desk.css PORTRAIT / components/brand/Portrait.tsx) so an
 * <img> that falls back here looks the same as a rendered <Portrait>. A brand's
 * mark is drawn as a logo-less brand print instead: its one initial, set large,
 * on paper (a PrintSticker's `mark` print). Colours are the desk tokens'
 * values (--paper, --manila, --ink-blue); a data URI cannot read CSS variables.
 * With no name the tile is left blank: a "?" reads as data ("unknown brand"),
 * which it is not.
 */
export function makeInitialsAvatar(name?: string, size = 64, mark = false): string {
  const letters = tileLetters(name, mark);
  const edge = Math.max(2, Math.round(size * 0.05));
  // sized by what the tile is, not by how many letters the name gave: a one-word creator
  // ("JerryRigEverything" → J) sits in a list beside two-letter ones and must match them
  const text = letters.length ? `<text x="50%" y="50%" dominant-baseline="central" text-anchor="middle"
      font-family="'Plus Jakarta Sans',system-ui,sans-serif" font-size="${size * (mark ? 0.46 : 0.3)}" font-weight="800" letter-spacing="-0.5" fill="#254a7c">
      ${letters.join('')}
    </text>` : '';
  // a logo sits in a paper frame (loaded logos are shown whole on white), so a mark's
  // ground is paper too; manila there reads as a second tile inset in the first
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <rect width="${size}" height="${size}" rx="3" fill="#fefdfa"/>
    <rect x="${edge}" y="${edge}" width="${size - edge * 2}" height="${size - edge * 2}" fill="${mark ? '#fefdfa' : '#faf6f0'}"/>
    ${text}
  </svg>`;
  return `data:image/svg+xml;base64,${utf8Base64(svg)}`;
}

/**
 * Wraps an external image URL through the backend proxy when the origin is
 * known to block cross-origin requests. Safe images (YouTube ggpht, our own
 * domain, data URIs, already-proxied URLs) are returned as-is.
 */
export function proxyImageUrl(url: string | null | undefined, fallbackName?: string): string {
  if (!url) return makeInitialsAvatar(fallbackName);

  if (url.startsWith('data:') || url.startsWith('blob:')) return url;

  // Local avatar served by the backend — prepend backend base URL
  if (url.startsWith('/api/media/')) return `${getBaseUrl()}${url}`;

  if (url.startsWith('/')) return url;

  const baseUrl = getBaseUrl();
  if (url.startsWith(baseUrl)) return url;

  const needsProxy = CORS_BLOCKED_PATTERNS.some(pattern => url.includes(pattern));

  if (!needsProxy) return url;

  return `${baseUrl}${PROXY_ENDPOINT}?url=${encodeURIComponent(url)}`;
}

/**
 * Default fallback image handler for <img> onError events.
 * Replaces the broken image with the initials tile for the name on the element:
 * data-name first, because a decorative <img> is rightly alt="" (its name is printed
 * beside it) and reading alt alone turned every failed logo into a "?". Then alt,
 * then a blank tile. data-mark says the image is a brand's logo: one initial.
 *
 *   <img src={logo} alt="" data-name={brand.name} data-mark onError={handleImageError} />
 */
export function handleImageError(e: React.SyntheticEvent<HTMLImageElement>) {
  const target = e.currentTarget;
  const { name, mark } = target.dataset;
  const fallback = makeInitialsAvatar(name?.trim() || target.alt, 64, mark !== undefined && mark !== 'false');
  // if the tile itself fails (a CSP without data:), its error lands here again: swap once
  if (target.src !== fallback) {
    target.src = fallback;
  }
}
